/* eslint-disable i18next/no-literal-string */
/* eslint-disable max-lines */
import { GetStaticProps, NextPage } from 'next';
import useTranslation from 'next-translate/useTranslation';

import styles from './revelation_order.module.scss';

import NextSeoWrapper from '@/components/NextSeoWrapper';
import PageContainer from '@/components/PageContainer';
import Link from '@/dls/Link/Link';
import { getAllChaptersData } from '@/utils/chapter';
import { getLanguageAlternates } from '@/utils/locale';
import { getCanonicalUrl, getSurahNavigationUrl } from '@/utils/navigation';
import REVELATION_ORDER from '@/utils/revelationOrder';
import Chapter from 'types/Chapter';
import ChaptersData from 'types/ChaptersData';

const PATH = '/revelation_order';

const REVELATION_NOTES: Record<number, string> = {
  68: 'Except 17-33 and 48-50, from Medina',
  73: 'Except 10, 11 and 20, from Medina',
  107: 'Only 1-3 from Mecca; the rest from Medina',
  53: 'Except 32, from Medina',
  77: 'Except 48, from Medina',
  50: 'Except 38, from Medina',
  54: 'Except 44-46, from Medina',
  7: 'Except 163-170, from Medina',
  36: 'Except 45, from Medina',
  25: 'Except 68-70, from Medina',
  19: 'Except 58 and 71, from Medina',
  20: 'Except 130 and 131, from Medina',
  56: 'Except 81 and 82, from Medina',
  26: 'Except 197 and 224-227, from Medina',
  28: 'Except 52-55 from Medina and 85 from Juhfa at the time of the Hijra',
  17: 'Except 26, 32, 33, 57, 73-80, from Medina',
  10: 'Except 40, 94, 95, 96, from Medina',
  11: 'Except 12, 17, 114, from Medina',
  12: 'Except 1, 2, 3, 7, from Medina',
  15: 'Except 87, from Medina',
  6: 'Except 20, 23, 91, 93, 114, 151, 152, 153, from Medina',
  31: 'Except 27-29, from Medina',
  40: 'Except 56, 57, from Medina',
  42: 'Except 23, 24, 25, 27, from Medina',
  43: 'Except 54, from Medina',
  45: 'Except 14, from Medina',
  46: 'Except 10, 15, 35, from Medina',
  18: 'Except 28, 83-101, from Medina',
  16: 'Except the last three verses from Medina',
  14: 'Except 28, 29, from Medina',
  32: 'Except 16-20, from Medina',
  30: 'Except 17, from Medina',
  29: 'Except 1-11, from Medina',
  2: 'Except 281 from Mina at the time of the Last Hajj',
  8: 'Except 30-36 from Mecca',
  47: "Except 13, revealed during the Prophet's Hijrah",
  22: 'Except 52-55, revealed between Mecca and Medina',
  48: 'Revealed while returning from Hudaybiyya',
  5: 'Except 3, revealed at Arafat on Last Hajj',
  9: 'Except last two verses from Mecca',
  110: 'Revealed at Mina on Last Hajj, but regarded as Medinan sura',
};

const REFERENCE_SURAH_NAMES = [
  'Al-Alaq',
  'Al-Qalam',
  'Al-Muzzammil',
  'Al-Muddaththir',
  'Al-Faatiha',
  'Al-Masad',
  'At-Takwir',
  "Al-A'laa",
  'Al-Lail',
  'Al-Fajr',
  'Ad-Dhuhaa',
  'Ash-Sharh',
  'Al-Asr',
  'Al-Aadiyaat',
  'Al-Kawthar',
  'At-Takaathur',
  "Al-Maa'un",
  'Al-Kaafiroon',
  'Al-Fil',
  'Al-Falaq',
  'An-Naas',
  'Al-Ikhlaas',
  'An-Najm',
  'Abasa',
  'Al-Qadr',
  'Ash-Shams',
  'Al-Burooj',
  'At-Tin',
  'Quraish',
  "Al-Qaari'a",
  'Al-Qiyaama',
  'Al-Humaza',
  'Al-Mursalaat',
  'Qaaf',
  'Al-Balad',
  'At-Taariq',
  'Al-Qamar',
  'Saad',
  "Al-A'raaf",
  'Al-Jinn',
  'Yaseen',
  'Al-Furqaan',
  'Faatir',
  'Maryam',
  'Taa-Haa',
  'Al-Waaqia',
  "Ash-Shu'araa",
  'An-Naml',
  'Al-Qasas',
  'Al-Israa',
  'Yunus',
  'Hud',
  'Yusuf',
  'Al-Hijr',
  "Al-An'aam",
  'As-Saaffaat',
  'Luqman',
  'Saba',
  'Az-Zumar',
  'Al-Ghaafir',
  'Fussilat',
  'Ash-Shura',
  'Az-Zukhruf',
  'Ad-Dukhaan',
  'Al-Jaathiya',
  'Al-Ahqaf',
  'Adh-Dhaariyat',
  'Al-Ghaashiya',
  'Al-Kahf',
  'An-Nahl',
  'Nooh',
  'Ibrahim',
  'Al-Anbiyaa',
  'Al-Muminoon',
  'As-Sajda',
  'At-Tur',
  'Al-Mulk',
  'Al-Haaqqa',
  "Al-Ma'aarij",
  'An-Naba',
  "An-Naazi'aat",
  'Al-Infitaar',
  'Al-Inshiqaaq',
  'Ar-Room',
  'Al-Ankaboot',
  'Al-Mutaffifin',
  'Al-Baqara',
  'Al-Anfaal',
  'Aal-i-Imraan',
  'Al-Ahzaab',
  'Al-Mumtahana',
  'An-Nisaa',
  'Az-Zalzala',
  'Al-Hadid',
  'Muhammad',
  "Ar-Ra'd",
  'Ar-Rahmaan',
  'Al-Insaan',
  'At-Talaaq',
  'Al-Bayyina',
  'Al-Hashr',
  'An-Noor',
  'Al-Hajj',
  'Al-Munaafiqoon',
  'Al-Mujaadila',
  'Al-Hujuraat',
  'At-Tahrim',
  'At-Taghaabun',
  'As-Saff',
  "Al-Jumu'a",
  'Al-Fath',
  'Al-Maaida',
  'At-Tawba',
  'An-Nasr',
];

type RevelationOrderPageProps = {
  chaptersData: ChaptersData;
};

const RevelationOrderPage: NextPage<RevelationOrderPageProps> = ({ chaptersData }) => {
  const { lang } = useTranslation('common');
  const chapters = REVELATION_ORDER.map((chapterId) => ({
    ...chaptersData[chapterId],
    id: chapterId,
  }))
    .filter(Boolean)
    .map((chapter) => chapter as Chapter);

  return (
    <>
      <NextSeoWrapper
        title="Revelation Order"
        url={getCanonicalUrl(lang, PATH)}
        languageAlternates={getLanguageAlternates(PATH)}
        description="The chronological order of the surahs of the Quran."
      />
      <PageContainer>
        <article className={styles.page}>
          <h1>Revelation Order</h1>
          <p>
            The chronological order of surahs is the order in which their first verses were revealed
            to the Prophet Muhammad, peace be upon him. This traditional order is based on reliable
            narrations and references to the history of the Quran.
          </p>
          <p>
            Note that a surah&apos;s place in this order is based on the revelation of its first
            verses, not necessarily the revelation of the whole surah.
          </p>

          <h2>Chronological Order of Surahs</h2>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <caption className={styles.visuallyHidden}>Chronological order of surahs</caption>
              <thead>
                <tr>
                  <th scope="col">Order</th>
                  <th scope="col">Sura Name</th>
                  <th scope="col">Number</th>
                  <th scope="col">Type</th>
                  <th scope="col">Note</th>
                </tr>
              </thead>
              <tbody>
                {chapters.map((chapter, index) => (
                  <tr key={chapter.id}>
                    <td>{index + 1}</td>
                    <td>
                      <Link href={getSurahNavigationUrl(chapter.id)} shouldPrefetch={false}>
                        {lang === 'en' ? REFERENCE_SURAH_NAMES[index] : chapter.transliteratedName}
                      </Link>
                    </td>
                    <td>{chapter.id}</td>
                    <td>{chapter.revelationPlace === 'makkah' ? 'Meccan' : 'Medinan'}</td>
                    <td>{REVELATION_NOTES[Number(chapter.id)] || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </article>
      </PageContainer>
    </>
  );
};

export const getStaticProps: GetStaticProps<RevelationOrderPageProps> = async ({ locale }) => ({
  props: {
    chaptersData: await getAllChaptersData(locale),
  },
});

export default RevelationOrderPage;
